import { ApolloError, gql } from '@apollo/client';
import { useCallback, useContext, useRef } from 'react';
import { buildCurrentAttachmentUids } from 'components/attachments';
import AccountContext from 'context/AccountContext';
import { updateCache } from 'utils/cacheUtils';
import Logger from 'utils/Logger';
import useUpdateSalesOrder from './useUpdateSalesOrder';

type Options = {
  onError?: (error: ApolloError) => void;
};

const UNIFIED_PERMIT_ATTACHMENTS = gql`
  fragment UnifiedPermitAttachmentsFragment on UnifiedPermit {
    attachments {
      uid
      fileName
      mimeType
      sizeBytes
    }
  }
`;

export const usePatchOrderAttachmentLinks = (
  options: Options = {}
): ((orderId: string, uids?: string[]) => Promise<void>) => {
  const {
    customer: { permits },
    refetchPermits,
  } = useContext(AccountContext);

  const permitsRef = useRef(permits);
  permitsRef.current = permits;
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  // Mirror the updated attachments onto the matching UnifiedPermit cache entry
  // so the persisted Apollo cache survives an app restart without a stale view.
  const { updateSalesOrder } = useUpdateSalesOrder({
    customUpdate: (cache, { data }) => {
      const updated = data?.updateSalesOrder;
      const orderId = updated?.uid;
      if (!orderId) return;
      const permit = permitsRef.current?.find((p) => p?.id === orderId);
      if (!permit?.orderLineIdWithVersion) return;
      updateCache(
        cache,
        {
          __typename: 'UnifiedPermit',
          id: orderId,
          orderLineIdWithVersion: permit.orderLineIdWithVersion,
        },
        {
          fragment: UNIFIED_PERMIT_ATTACHMENTS,
          data: { attachments: updated.attachments ?? [] },
        }
      );
    },
  });

  return useCallback(
    async (orderId: string, uids?: string[]) => {
      const attachmentUids = uids ?? buildCurrentAttachmentUids(orderId);

      const orderLine = permitsRef.current?.find((p) => p?.id === orderId)
        ?.orderLines?.[0];

      try {
        const result = await updateSalesOrder({
          variables: {
            input: {
              salesOrderId: orderId,
              attachmentUids,
              updateType: 'PATCH',
              ...(orderLine?.pmcId ? { pmcId: orderLine.pmcId } : {}),
              ...(orderLine?.orderLineId
                ? { orderLineId: orderLine.orderLineId }
                : {}),
              ...(orderLine?.version != null
                ? { version: orderLine.version }
                : {}),
              ...(orderLine?.quantity != null
                ? { quantity: orderLine.quantity }
                : {}),
            },
          },
        });

        if (result.errors?.length) {
          onErrorRef.current?.(
            new ApolloError({ graphQLErrors: [...result.errors] })
          );
          return;
        }
      } catch (e) {
        const error =
          e instanceof ApolloError
            ? e
            : new ApolloError({
                networkError: e instanceof Error ? e : new Error(String(e)),
              });
        Logger.error(
          `updateSalesOrder attachment PATCH failed | orderId=${orderId} | ${error.message}`
        );
        onErrorRef.current?.(error);
        return;
      }

      try {
        await refetchPermits();
      } catch (e) {
        Logger.error(
          `refetchPermits after attachment-link PATCH failed | orderId=${orderId} | ${e instanceof Error ? e.message : String(e)}`
        );
      }
    },
    [updateSalesOrder, refetchPermits]
  );
};
